import { nextTick } from "vue";
import { cloneDeep } from "@/shared/utils/helpers";
import { iframeMutations } from "@/shared/store/iframe.mutations";
import { timerService } from "@/apps/timer/services/timer.service";
import { projectsService } from "@/shared/projects/projects.service";
import { makeLogger } from "@/shared/consola";
import { timerSnapshotService } from "@/apps/timer/store/timer-snapshot.service";
import { prepareTimer } from "@/shared/projects/prepareData";

const logger = makeLogger("timer.store");

export default {
    namespaced: true,
    state: () => ({
        timer: {},
        iframe: null,
        iframeLoaded: false,
        labelTranslations: {},
        hasChanges: false,
        hasSnapshot: false,
    }),
    getters: {
        appUrl: (s, g, rootS, rootGetters) => rootGetters.easyUrls.timer,
        langLabels: (state) =>
            state.labelTranslations[state.timer.language] || {
                days: "days",
                hours: "hours",
                minutes: "minutes",
                seconds: "seconds",
            },
    },
    mutations: {
        ...iframeMutations,
        setProject(state, { timer, snapshot = {} }) {
            state.timer = timerSnapshotService.makeSnapshotState(state, "timer", timer, snapshot);
        },
        clearProject(state) {
            state.timer = {};
            timerSnapshotService.clearSnapshot(state, "timer", {});
        },
    },
    actions: {
        async loadLabelTranslations({ state }, force = false) {
            if (!force && Object.keys(state.labelTranslations).length) {
                logger.debug("loadLabelTranslations not fired, labels already loaded !");
                return;
            }
            try {
                state.labelTranslations = (await timerService.getAllLanguagesLabels()) || {};
                logger.debug(`loadLabelTranslations refreshed forced: ${force}`);
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        refreshIframe({ state, getters }) {
            const timer = cloneDeep(state.timer);
            timer.langLabels = cloneDeep(getters.langLabels);
            state.iframe = document.querySelector("#iframe");
            if (state.iframe && timer.id) {
                state.iframe.contentWindow.postMessage({ timerData: timer }, getters.appUrl);
            }
        },
        async saveSnapshot({ rootState, state, dispatch }) {
            rootState.waiting = true;
            try {
                await timerSnapshotService.updateSnapshotAfterSave(state.timer.uuid, {
                    stateSlice: state,
                    actionToTakeIfNotRequired: () => dispatch("mergeSnapshotChanges"),
                });
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async mergeSnapshotChanges({ state, rootState, commit }) {
            await nextTick();
            rootState.waiting = true;

            const { uuid } = state.timer;
            const timer = JSON.parse(JSON.stringify(state.timer));
            delete timer.id;
            delete timer.createdAt;
            delete timer.updatedAt;
            //FIXME: quickfix for transparent value
            if (timer.transparent === null) {
                timer.transparent = 0;
            }

            if (!state.timer.id) {
                return; //@quickfix: issue with nonexistent id (racing condition?)
            }

            try {
                const updatedTimer = await projectsService.update({
                    tool: "timer",
                    uuid,
                    data: timer,
                });

                state.hasSnapshot = false;

                commit("showNotify", "autoSave", { root: true });
                commit("setProject", { timer: prepareTimer(updatedTimer), snapshot: {} });
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async loadProject({ rootState, commit }, uuid) {
            rootState.waiting = true;
            try {
                const { project, snapshot } = await projectsService.getOneWithSnapshot({
                    tool: "timer",
                    uuid,
                });
                const timer = prepareTimer(project);
                commit("setProject", { timer, snapshot });
                rootState.waiting = false;
            } catch {
                rootState.waiting = false;
            }
        },
        async unpublishTimer({ state, commit, dispatch }) {
            await dispatch("unpublish", { uuid: state.timer.uuid });
            commit("setUnpublished", state.timer, { root: true });
            dispatch("mergeSnapshotChanges");
        },
        async unpublish(_, { uuid }) {
            try {
                await projectsService.publish({
                    tool: "timer",
                    uuid,
                    publish: false,
                });
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async publishProject({ state, rootState, commit, rootGetters, dispatch }) {
            if (!rootGetters.logged) {
                dispatch("showUnloggedClickModal", null, { root: true });
                return;
            }

            rootState.waiting = true;

            await nextTick();
            const { timer = "", htmlTimer = "" } = await projectsService.publish({
                tool: "timer",
                uuid: state.timer.uuid,
                publish: true,
            });
            commit("setPublished", state.timer, { root: true });

            await dispatch("mergeSnapshotChanges");
            rootState.waiting = false;
            return { timer, htmlTimer };
        },
        changeFontFamily({ state }, payload) {
            state.timer.fontFamily = payload;
        },
    },
};
